<template>
    <DModal
        :title="__('messages.list_configuration')"
        is-confirm
        is-big
        :show="showModal"
        :is-awaiting-validation="true"
        @confirm="submitForm"
        @close="$emit('close-modal')"
    >
        <DCard padding>
            <DCol
                sm="12"
                class="pb-3"
            >
                <DButton
                    :label="__('settings.list_view.set_default_configuration')"
                    @click="setDefaultValues"
                />
            </DCol>
            <DListGroup>
                <DListGroupItem class="d-flex justify-content-between align-items-center group-header">
                    <div class="div-column-name">
                        {{ __('settings.list_view.column_name') }}
                    </div>
                    <div class="div-column-width">
                        {{ __('settings.list_view.width') }}
                    </div>
                    <div class="div-show-in-list">
                        {{ __('settings.list_view.show_in_list') }}
                    </div>
                    <div class="div-sort-supported">
                        {{ __('settings.list_view.sort_enabled') }}
                    </div>
                    <div class="div-actions" />
                </DListGroupItem>
                <VueNestable
                    v-model="columns"
                    key-prop="key"
                    :max-depth="1"
                >
                    <template #default="{ item }">
                        <DListGroupItem
                            class="d-flex justify-content-between align-items-center group-body"
                            :class="item.show_column === false ? 'bg-light' : ''"
                        >
                            <div
                                class="div-column-name"
                            >
                                {{ __('settings.list_view.' + item.list + '.' + item.column) }}
                            </div>
                            <div
                                class="div-column-width"
                            >
                                <DInput
                                    :id="`show-in-list-${item.key}`"
                                    v-model="item.width"
                                    type="number"
                                    :min="1"
                                    :max="1000"
                                />
                            </div>
                            <div class="div-show-in-list">
                                <DSwitch
                                    v-if="item.column !== 'actions'"
                                    :id="`show-in-list-${item.key}`"
                                    v-model="item.show_column"
                                    color="success"
                                    variant="3d"
                                    :disabled="item.is_required === true"
                                />
                            </div>
                            <div
                                class="div-sort-supported"
                                :class="item.sort_column_supported === false ? 'not-allowed' : ''"
                            >
                                <DSwitch
                                    v-if="item.column !== 'actions' && item.column !== 'icon'"
                                    :id="`sort-supported-${item.key}`"
                                    v-model="item.sort_column_enabled"
                                    color="success"
                                    variant="3d"
                                    :disabled="item.sort_column_supported === false"
                                />
                            </div>
                            <div
                                class="div-actions"
                            >
                                <div :class="item.show_column === true ? 'actions-body' : 'actions-inactive-body'">
                                    <!-- Handler -->
                                    <VueNestableHandle :item="item">
                                        <div class="meta">
                                            <div>
                                                <DButton
                                                    icon="drag-variant"
                                                    mini
                                                    drag-and-drop-btn
                                                />
                                            </div>
                                            <div />
                                        </div>
                                    </VueNestableHandle>
                                </div>
                            </div>
                        </DListGroupItem>
                    </template>
                </VueNestable>
            </DListGroup>
        </DCard>
    </DModal>
</template>

<script setup>
import { VueNestable, VueNestableHandle } from '@sparkz-community/vue3-nestable'
import { __ } from '~@/Composables/translation'
import { ref } from 'vue'
import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'

const props = defineProps({
    showModal: {
        type: Boolean,
        default: false
    },
    userListView: {
        type: Array,
        default: () => {
            return []
        }
    }
})

const columns = ref([])

setColumns()

const emit = defineEmits(['close-modal'])

function setColumns () {
    for (const userList of props.userListView) {
        columns.value.push({
            key: userList.order_column_no,
            list: userList.list,
            column: userList.column,
            width: userList.width,
            show_column: userList.show_column,
            sort_column_enabled: userList.sort_column_enabled,
            sort_column_supported: userList.sort_column_supported,
            order_column_no: userList.order_column_no,
            is_required: userList.is_required,
        })
    }
}

function submitForm () {
    if (columns.value.length === 0) {
        return
    }

    const form = useForm({
        columns: flattenColumns(columns.value)
    })

    form.post(route('settings.list-view-settings.store'))

    emit('close-modal')
}

function setDefaultValues () {
    const form = useForm({
        list: props.userListView[0].list,
        redirectUrl: props.redirectUrl,
        customerId: props.customerId
    })

    form.post(route('settings.list-view-settings.set-default-values'))

    emit('close-modal')
}

function flattenColumns (columns) {
    if (columns.length === 0) {
        return {}
    }

    const result = {}

    for (const [key, column] of Object.entries(columns)) {
        result[key] = JSON.parse(JSON.stringify(column))
    }

    return result
}

</script>

<style lang="scss">
/*
* Style for nestable
*/
.nestable {
    position: relative;
}
.nestable-rtl {
    direction: rtl;
}
.nestable .nestable-list {
    margin: 0;
    padding: 0 0 0 40px;
    list-style-type: none;
}
.nestable-rtl .nestable-list {
    padding: 0 40px 0 0;
}
.nestable > .nestable-list {
    padding: 0;
}
.nestable-item:first-child,
.nestable-item-copy:first-child {
    margin-top: 0;
}
.nestable-item {
    position: relative;
}
.nestable-item.is-dragging .nestable-list {
    pointer-events: none;
}
.nestable-item.is-dragging * {
    opacity: 0;
    filter: alpha(opacity=0);
}
.nestable-item.is-dragging:before {
    content: ' ';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(106, 127, 233, 0.274);
    border: 1px dashed rgb(73, 100, 241);
    -webkit-border-radius: 5px;
    border-radius: 5px;
}
.nestable-drag-layer {
    position: fixed;
    top: 0;
    left: 0;
    z-index: 100;
    pointer-events: none;
}
.nestable-rtl .nestable-drag-layer {
    left: auto;
    right: 0;
}
.nestable-drag-layer > .nestable-list {
    position: absolute;
    top: 0;
    left: 0;
    padding: 0;
    background-color: rgba(106, 127, 233, 0.274);
}
.nestable-rtl .nestable-drag-layer > .nestable-list {
    padding: 0;
}
.nestable-handle {
    display: inline;
}
</style>

<style scoped lang="scss">
.group-header {
    border-bottom: 1px solid rgba(0,0,0,0.14);
    align-items: center;

    & > div {
        font-weight: bold;
    }
}
.group-body {
    border-bottom: 1px solid rgba(0,0,0,0.14) !important;
    align-items: center !important;
}
.div-column-name {
    width: 22.5% !important;
    padding-right: 10px !important;
}
.div-column-width {
    width: 22.5% !important;
    display: flex !important;
    justify-content: center !important;

    & > input {
        width: 150px;
    }
}
.div-show-in-list {
    width: 22.5% !important;
    display: flex !important;
    justify-content: center !important;
}
.div-sort-supported {
    width: 22.5% !important;
    display: flex !important;
    justify-content: center !important;
}
.div-actions {
    width: 10% !important;
}
.actions-body {
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 7px;
    align-items: center;
    padding: 0 !important;
    background: var(--colorAppSurface) !important;
}
.actions-inactive-body {
    display: grid;
    grid-template-columns: auto auto;
    grid-gap: 7px;
    align-items: center;
    padding: 0 !important;
    background: #ebedef !important;
}
.form-switch {
    padding: 0 !important;
    background: var(--colorAppSurface) !important;
}
.not-allowed {
    cursor: not-allowed;
}
</style>
